// Admin → Users (SPEC §7.8): list with role filter, add account, deactivate/activate, reset password.

import type { ApiResult } from "../../api/client";
import { fetchTeachers } from "../../api/teachers";
import { createUser, fetchUsers, resetPassword, setUserActive } from "../../api/users";
import type { Role, Teacher, UserAccount } from "../../api/types";
import { currentLocale, formatTimestamp, t } from "../../i18n/i18n";
import { navigate, setRedraw, type RouteMatch } from "../../router";
import { requireSchool } from "../../session";
import { sortByName } from "../../text";
import { createButton, createElement } from "../../ui/dom";
import { createErrorBox, showProblem, showToast } from "../../ui/feedback";
import { createInputField, createSelectField, showFieldErrors, type Field } from "../../ui/forms";
import { reportAndReload } from "../../ui/resultMessage";
import { createLoading, showPageProblem, startPage } from "../pageFrame";

const ROLES: Role[] = ["Admin", "Viewer", "Teacher"];

interface UsersView {
  users: UserAccount[];
  teachers: Teacher[];
  role: Role | null;
  newTeacherId: string | null;
}

export async function renderUsersPage(container: HTMLElement, route: RouteMatch): Promise<void> {
  const page: HTMLElement = startPage(container, t("admin.users"));
  page.append(createLoading());
  const role: Role | null = parseRole(route.query.get("role"));
  const users: ApiResult<UserAccount[]> = await fetchUsers(role);
  const teachers: ApiResult<Teacher[]> = await fetchTeachers(true);
  if (users.ok === false) {
    showPageProblem(page, users.problem);
    return;
  }

  const view: UsersView = {
    users: users.data, teachers: teachers.ok ? teachers.data : [], role, newTeacherId: route.query.get("newTeacher"),
  };
  const draw = (): void => {
    drawPage(startPage(container, t("admin.users")), view);
  };
  setRedraw(container, draw);
  draw();
}

function parseRole(text: string | null): Role | null {
  return ROLES.find((role: Role): boolean => role === text) ?? null;
}

function drawPage(page: HTMLElement, view: UsersView): void {
  const roleOptions: { value: string; label: string }[] = [{ value: "", label: t("admin.allRoles") }, ...ROLES.map(
    (role: Role): { value: string; label: string } => ({ value: role, label: t(`roles.${role}`) }))];
  const filter: Field<HTMLSelectElement> = createSelectField(t("admin.role"), roleOptions, view.role ?? "");
  filter.input.addEventListener("change", (): void => {
    navigate(filter.input.value === "" ? "/admin/users" : `/admin/users?role=${filter.input.value}`);
  });
  const table: HTMLTableElement = buildTable(view);
  const wrapper: HTMLDivElement = createElement("div", "table-scroll");
  wrapper.append(table);
  const toolbar: HTMLDivElement = createElement("div", "toolbar");
  toolbar.append(filter.wrapper);
  page.append(toolbar, wrapper, buildAddForm(view));
}

function buildTable(view: UsersView): HTMLTableElement {
  const table: HTMLTableElement = createElement("table");
  const head: HTMLTableRowElement = table.createTHead().insertRow();
  for (const title of [t("admin.username"), t("admin.displayName"), t("admin.role"), t("admin.linkedTeacher"),
    t("admin.status"), t("admin.lastLogin"), ""]) {
    head.append(createElement("th", "", title));
  }

  const body: HTMLTableSectionElement = table.createTBody();
  for (const user of view.users) {
    body.append(buildRow(user, view.teachers));
  }

  return table;
}

function buildRow(user: UserAccount, teachers: Teacher[]): HTMLTableRowElement {
  const row: HTMLTableRowElement = createElement("tr");
  const teacher: Teacher | undefined = teachers.find((candidate: Teacher): boolean => candidate.id === user.teacherId);
  const texts: string[] = [user.username, user.displayName, t(`roles.${user.role}`),
    teacher === undefined ? "" : `${teacher.firstName} ${teacher.lastName}`,
    user.active ? t("teachers.active") : t("teachers.inactive"),
    user.lastLoginAt === null ? t("app.none") : formatTimestamp(user.lastLoginAt, requireSchool().timeZone)];
  for (const text of texts) {
    row.append(createElement("td", "", text));
  }

  const actions: HTMLDivElement = createElement("div", "row-actions");
  row.insertCell().append(actions);
  actions.append(createButton(user.active ? t("admin.deactivate") : t("admin.activate"), async (): Promise<void> => {
    await reportAndReload(await setUserActive(user.id, user.active === false));
  }), buildResetPassword(user));
  return row;
}

function buildResetPassword(user: UserAccount): HTMLElement {
  const input: HTMLInputElement = createElement("input");
  input.type = "password";
  input.placeholder = t("admin.newPassword");
  input.autocomplete = "new-password";
  const wrapper: HTMLSpanElement = createElement("span", "row-actions");
  wrapper.append(input, createButton(t("admin.resetPassword"), async (): Promise<void> => {
    const result: ApiResult<null> = await resetPassword(user.id, input.value);
    if (result.ok) {
      showToast(t("admin.passwordReset"));
      input.value = "";
      return;
    }

    await reportAndReload(result);
  }));
  return wrapper;
}

/** The add form; "Create login" on a teacher page opens it with role Teacher and the teacher chosen (SPEC §7.8). */
function buildAddForm(view: UsersView): HTMLElement {
  const fields: AddUserFields = createAddUserFields(view);
  const errorBox: HTMLDivElement = createErrorBox();
  const save: HTMLButtonElement = createButton(t("admin.addUser"), async (): Promise<void> => {
    const result: ApiResult<UserAccount> = await createUser({
      username: fields.username.input.value, displayName: fields.displayName.input.value,
      role: fields.role.input.value as Role, teacherId: fields.teacher.input.value || null,
      initialPassword: fields.password.input.value,
    });
    showProblem(errorBox, result.ok ? null : result.problem);
    showFieldErrors(result.ok ? null : result.problem, { username: fields.username, displayName: fields.displayName,
      teacherId: fields.teacher, initialPassword: fields.password });
    if (result.ok) {
      navigate("/admin/users");
    }
  }, "button-primary");
  const grid: HTMLDivElement = createElement("div", "form-grid");
  grid.append(fields.username.wrapper, fields.displayName.wrapper, fields.role.wrapper, fields.teacher.wrapper,
    fields.password.wrapper);
  const card: HTMLDivElement = createElement("div", "card");
  card.append(createElement("h2", "", t("admin.addUser")), grid, errorBox, save);
  return card;
}

interface AddUserFields {
  username: Field<HTMLInputElement>;
  displayName: Field<HTMLInputElement>;
  role: Field<HTMLSelectElement>;
  teacher: Field<HTMLSelectElement>;
  password: Field<HTMLInputElement>;
}

function createAddUserFields(view: UsersView): AddUserFields {
  const roles: { value: string; label: string }[] = ROLES.map(
    (role: Role): { value: string; label: string } => ({ value: role, label: t(`roles.${role}`) }));
  const available: Teacher[] = view.teachers.filter((teacher: Teacher): boolean =>
    teacher.archived === false && (teacher.login === null || teacher.login.active === false));
  const teacherOptions: { value: string; label: string }[] = [{ value: "", label: t("app.none") },
    ...sortByName(available, currentLocale()).map((teacher: Teacher): { value: string; label: string } => ({
      value: teacher.id, label: `${teacher.lastName} ${teacher.firstName}` }))];
  const password: Field<HTMLInputElement> = createInputField(t("admin.initialPassword"), "password", "");
  password.input.autocomplete = "new-password";
  return {
    username: createInputField(t("admin.username"), "text", ""),
    displayName: createInputField(t("admin.displayName"), "text", ""),
    role: createSelectField(t("admin.role"), roles, view.newTeacherId === null ? "Viewer" : "Teacher"),
    teacher: createSelectField(t("admin.linkedTeacher"), teacherOptions, view.newTeacherId ?? ""),
    password,
  };
}
